// The DFT module's physics tabs (G-3): Magnetism (a starting magnetisation per atom, the ferro / antiferromagnetic
// orders of the cell, a supercell for orders that need one), DFT+U (a U per species with suggestions) and the band
// path (nodes from the cell's special points, counts, breaks, the standard path). Each change makes a new recipe,
// which the checks then follow; the server does what needs the crystal's symmetry.
import { ActionIcon, Alert, Badge, Button, Checkbox, Group, List, MultiSelect, NumberInput, ScrollArea, Select, Stack,
  Table, Text, Tooltip } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { atomRows, editHubbard, editKpoints, editMoments, editOrders, editSupercell, elementOf, HUBBARD_PROJECTORS,
  pathRows, siteMoments, spreadPoints, withHubbard, withPath, type MagneticOrder, type PathRow, type Recipe,
  type SiteMoment } from "../../lib/qe";

const MAGNETIC = new Set(["Ti", "V", "Cr", "Mn", "Fe", "Co", "Ni", "Cu", "Mo", "Ru", "Rh", "Os", "Ir", "Ce", "Pr", "Nd", "Pm",
  "Sm", "Eu", "Gd", "Tb", "Dy", "Ho", "Er", "Tm", "Yb", "U", "Np", "Pu"]);

function Notes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return <Alert color="blue" p="xs"><List size="xs" spacing={2}>{notes.map((n) => <List.Item key={n}>{n}</List.Item>)}</List></Alert>;
}

const arrows = (signs: number[]) => signs.map((s) => (s > 0 ? "↑" : "↓")).join("");

// == magnetism ==========================================================================================================
export function MagnetismPanel({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const atoms = atomRows(recipe);
  const current = useMemo(() => siteMoments(recipe), [recipe]);
  const [moments, setMoments] = useState<SiteMoment[]>(current);
  useEffect(() => setMoments(current), [JSON.stringify(current)]);
  const noncollinear = recipe.spin === "noncollinear" || recipe.spin === "soc";
  const present = [...new Set(atoms.map((a) => elementOf(a.label)))];
  const [elements, setElements] = useState<string[]>(present.filter((e) => MAGNETIC.has(e)));
  const [magnitude, setMagnitude] = useState(0.5);
  const [cell, setCell] = useState<[number, number, number]>([2, 1, 1]);
  const [notes, setNotes] = useState<string[]>([]);
  const done = (r: { recipe: Recipe; notes: string[] }) => { setNotes(r.notes); onChange(r.recipe); };
  const apply = useMutation({ mutationFn: (m: (SiteMoment | number)[]) => editMoments(recipe, m), onSuccess: done });
  const orders = useMutation({ mutationFn: () => editOrders(recipe, elements, magnitude) });
  const supercell = useMutation({ mutationFn: (m: number[] | number[][]) => editSupercell(recipe, m),
    onSuccess: (r) => { orders.reset(); done(r); } });
  const dirty = JSON.stringify(moments) !== JSON.stringify(current);
  const setM = (i: number, patch: Partial<SiteMoment>) => setMoments((ms) => ms.map((m, k) => (k === i ? { ...m, ...patch } : m)));
  const busy = apply.isPending || supercell.isPending;
  return (
    <Stack gap="sm" data-testid="magnetism">
      <Text size="xs" c="dimmed">The starting magnetisation of each atom (Quantum ESPRESSO's starting_magnetization: a fraction of its
        valence charge, −1 … 1; the run finds the moments). Atoms of one element with different values become species of their
        own (Fe1, Fe2, …) with the same pseudopotential and U. Spin is switched on when needed (Settings: Spin).</Text>

      <Stack gap={4}>
        <Group gap="xs" align="flex-end">
          <MultiSelect size="xs" w={260} label="Magnetic elements" data={present} value={elements} onChange={setElements}
            placeholder="choose the elements with moments" />
          <NumberInput size="xs" w={150} label="Starting magnetisation" value={magnitude} min={0.05} max={1} step={0.05}
            decimalScale={2} onChange={(v) => typeof v === "number" && setMagnitude(v)} />
          <Button size="xs" variant="light" onClick={() => orders.mutate()} loading={orders.isPending} disabled={!elements.length}>
            Find the magnetic orders</Button>
        </Group>
        <ErrorBox error={orders.error} />
        {orders.data && (
          <Stack gap={4} data-testid="magnetic-orders">
            <Notes notes={orders.data.notes} />
            <Table fz="xs" verticalSpacing={2} withRowBorders={false}>
              <Table.Tbody>
                {orders.data.orders.map((o: MagneticOrder) => (
                  <Table.Tr key={o.name}>
                    <Table.Td><Badge size="xs" variant="light" color={o.kind === "FM" ? "red" : o.kind === "AFM" ? "blue" : "grape"}>
                      {o.kind}</Badge></Table.Td>
                    <Table.Td>{o.name}</Table.Td>
                    <Table.Td ff="monospace">{arrows(o.signs)}</Table.Td>
                    <Table.Td>{o.spacegroup}</Table.Td>
                    <Table.Td><Button size="compact-xs" variant="default" onClick={() => apply.mutate(o.moments)} loading={apply.isPending}
                      aria-label={`use ${o.name}`}>Use</Button></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed">The arrows follow the magnetic atoms in the order of the table below. Orders that differ only by a
              symmetry of the crystal or by flipping every moment are listed once, the most symmetric first.</Text>
          </Stack>
        )}
      </Stack>

      <Group gap="xs" align="flex-end">
        {[0, 1, 2].map((i) => (
          <NumberInput key={i} size="xs" w={70} label={`n${i + 1}`} value={cell[i]} min={1} max={6} allowDecimal={false}
            onChange={(v) => typeof v === "number" && setCell((c) => c.map((x, j) => (j === i ? v : x)) as [number, number, number])} />
        ))}
        <Button size="xs" variant="default" loading={supercell.isPending && !Array.isArray(supercell.variables?.[0])}
          onClick={() => supercell.mutate(cell)}>Make a supercell</Button>
        <Tooltip label="The √2 × √2 cell in the a1-a2 plane (a1+a2, −a1+a2): the checkerboard order of a square lattice">
          <Button size="xs" variant="default" loading={supercell.isPending && Array.isArray(supercell.variables?.[0])}
            onClick={() => supercell.mutate([[1, 1, 0], [-1, 1, 0], [0, 0, 1]])}>√2 × √2 cell</Button>
        </Tooltip>
      </Group>
      <ErrorBox error={supercell.error ?? apply.error} />
      <Notes notes={notes} />

      <ScrollArea.Autosize mah={320}>
        <Table fz="xs" verticalSpacing={2} withRowBorders={false} data-testid="moments-table">
          <Table.Thead><Table.Tr><Table.Th>#</Table.Th><Table.Th>Species</Table.Th><Table.Th>Position</Table.Th>
            <Table.Th>Starting magnetisation</Table.Th>{noncollinear && <><Table.Th>θ (°)</Table.Th><Table.Th>φ (°)</Table.Th></>}
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {atoms.map((a, i) => (
              <Table.Tr key={i}>
                <Table.Td>{i + 1}</Table.Td>
                <Table.Td>{a.label}</Table.Td>
                <Table.Td>{[a.x, a.y, a.z].map((v) => v.toFixed(3)).join(", ")}</Table.Td>
                <Table.Td><NumberInput size="xs" w={110} value={moments[i]?.m ?? 0} min={-1} max={1} step={0.1} decimalScale={3}
                  aria-label={`moment of atom ${i + 1}`} onChange={(v) => typeof v === "number" && setM(i, { m: v })} /></Table.Td>
                {noncollinear && <>
                  <Table.Td><NumberInput size="xs" w={80} value={moments[i]?.theta ?? ""} min={0} max={180} step={15}
                    aria-label={`theta of atom ${i + 1}`} onChange={(v) => setM(i, { theta: typeof v === "number" ? v : null })} /></Table.Td>
                  <Table.Td><NumberInput size="xs" w={80} value={moments[i]?.phi ?? ""} min={-360} max={360} step={15}
                    aria-label={`phi of atom ${i + 1}`} onChange={(v) => setM(i, { phi: typeof v === "number" ? v : null })} /></Table.Td>
                </>}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      <SpinNote recipe={recipe} />
      <Group gap="xs">
        <Button size="xs" onClick={() => apply.mutate(moments)} disabled={!dirty || busy} loading={apply.isPending}>Apply these moments</Button>
        <Button size="xs" variant="subtle" disabled={busy} onClick={() => apply.mutate(atoms.map(() => 0))}>No moments</Button>
        {dirty && <Button size="xs" variant="subtle" onClick={() => setMoments(current)}>Undo the changes</Button>}
      </Group>
    </Stack>
  );
}

// == DFT+U ==============================================================================================================
export function HubbardPanel({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const labels = Object.keys(recipe.species ?? {}).join(",");
  const q = useQuery({ queryKey: ["dft-hubbard", labels, JSON.stringify(recipe.hubbard ?? null)], queryFn: () => editHubbard(recipe),
    placeholderData: (p) => p });
  const projector = recipe.hubbard?.projector ?? "ortho-atomic";
  const u: Record<string, number | null> = { ...(recipe.hubbard?.u ?? {}) };
  const set = (key: string, v: number | null) => onChange(withHubbard(recipe, projector, { ...u, [key]: v }));
  const rows = q.data?.species ?? [];
  return (
    <Stack gap="sm" data-testid="hubbard">
      <Text size="xs" c="dimmed">DFT+U (Dudarev): a U on the d or f shell of a species, written as Quantum ESPRESSO's HUBBARD card
        (version 7.1 or newer). The suggestions are starting points - the Materials Project's values fitted for oxides, and the value
        usual for ceria - not answers: U depends on the compound and the projector.</Text>
      <ErrorBox error={q.error} />
      {q.data && !rows.length && <Text size="sm" c="dimmed">No species with d or f electrons: DFT+U does not apply.</Text>}
      {rows.length > 0 && (
        <>
          <Select size="xs" w={200} label="Projector" data={HUBBARD_PROJECTORS} value={projector} allowDeselect={false}
            onChange={(v) => v && onChange(withHubbard(recipe, v, u))} />
          <Table fz="xs" verticalSpacing={3} withRowBorders={false}>
            <Table.Thead><Table.Tr><Table.Th>Species</Table.Th><Table.Th>Shell</Table.Th><Table.Th>U (eV)</Table.Th>
              <Table.Th>Suggested</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const key = `${r.species}-${r.manifold}`;
                return (
                  <Table.Tr key={key}>
                    <Table.Td>{r.species}</Table.Td>
                    <Table.Td>{r.manifold}</Table.Td>
                    <Table.Td><NumberInput size="xs" w={100} value={u[key] ?? ""} min={0} max={15} step={0.1} decimalScale={2}
                      placeholder="none" aria-label={`U of ${key}`} onChange={(v) => set(key, typeof v === "number" ? v : null)} /></Table.Td>
                    <Table.Td>{r.suggested != null
                      ? <Group gap={6}><Text size="xs">{r.suggested} eV <Text span c="dimmed" size="xs">({r.source})</Text></Text>
                          {u[key] !== r.suggested && <Button size="compact-xs" variant="subtle" onClick={() => set(key, r.suggested)}>Use</Button>}</Group>
                      : <Text size="xs" c="dimmed">–</Text>}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          {Object.values(u).some((v) => v) && <Button size="xs" variant="subtle" w="fit-content"
            onClick={() => onChange(withHubbard(recipe, projector, {}))}>No DFT+U</Button>}
        </>
      )}
    </Stack>
  );
}

// == the band path ======================================================================================================
export function PathEditor({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const cellKey = JSON.stringify(recipe.structure ?? null);
  const k = useQuery({ queryKey: ["dft-kpoints", cellKey], queryFn: () => editKpoints(recipe), staleTime: 300_000 });
  const rows = pathRows(recipe);
  const units = recipe?.bands?.path?.units ?? "crystal_b";
  const [total, setTotal] = useState(150);
  const set = (next: PathRow[]) => onChange(withPath(recipe, next));
  const edit = (i: number, patch: Partial<PathRow>) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const special = k.data?.points ?? [];
  const labelData = special.map((p) => ({ value: p.label, label: `${p.label}  (${p.k.map((x) => +x.toFixed(4)).join(", ")})${p.in_plane ? "" : " · out of plane"}` }));
  const move = (i: number, d: number) => {
    const next = [...rows];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    set(next);
  };
  const points = rows.slice(0, -1).reduce((s, r) => s + r.n, 0) + (rows.length ? 1 : 0);
  if (units !== "crystal_b")
    return <Text size="sm" c="dimmed">This path is in {units} units: edit it in the Recipe (YAML) tab, or use the standard path
      (<Button size="compact-xs" variant="subtle" disabled={!k.data} onClick={() => k.data && onChange({ ...recipe,
        bands: { ...(recipe.bands ?? {}), path: { units: "crystal_b", points: k.data.standard } } })}>standard path</Button>).</Text>;
  return (
    <Stack gap="sm" data-testid="path-editor">
      <Text size="xs" c="dimmed">The k-path of the bands run: its nodes in fractions of the reciprocal lattice vectors, the points from
        each node to the next, and "jump" to end a branch there (the next node starts a new one). Pick a node from the cell's special
        points{k.data ? ` (${k.data.dimensionality})` : ""}, or type coordinates.</Text>
      <ErrorBox error={k.error} />
      <ScrollArea.Autosize mah={360}>
        <Table fz="xs" verticalSpacing={2} withRowBorders={false}>
          <Table.Thead><Table.Tr><Table.Th>Label</Table.Th><Table.Th>k1</Table.Th><Table.Th>k2</Table.Th><Table.Th>k3</Table.Th>
            <Table.Th>Points to the next</Table.Th><Table.Th>Jump</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((r, i) => {
              const last = i === rows.length - 1;
              return (
                <Table.Tr key={i}>
                  <Table.Td><Select size="xs" w={110} data={labelData.some((d) => d.value === r.label) ? labelData
                    : [{ value: r.label, label: r.label || "(none)" }, ...labelData]} value={r.label} searchable allowDeselect={false}
                    aria-label={`label of node ${i + 1}`} comboboxProps={{ width: 260 }}
                    onChange={(v) => { const p = special.find((s) => s.label === v); if (v) edit(i, p ? { label: v, k: [...p.k] } : { label: v }); }} /></Table.Td>
                  {[0, 1, 2].map((j) => (
                    <Table.Td key={j}><NumberInput size="xs" w={85} value={r.k[j]} step={0.05} decimalScale={6} hideControls
                      aria-label={`k${j + 1} of node ${i + 1}`}
                      onChange={(v) => typeof v === "number" && edit(i, { k: r.k.map((x, m) => (m === j ? v : x)) })} /></Table.Td>
                  ))}
                  <Table.Td>{last ? <Text size="xs" c="dimmed">end</Text> : r.n === 1 ? <Text size="xs" c="dimmed">–</Text>
                    : <NumberInput size="xs" w={80} value={r.n} min={2} max={500} allowDecimal={false} aria-label={`points after node ${i + 1}`}
                      onChange={(v) => typeof v === "number" && edit(i, { n: v })} />}</Table.Td>
                  <Table.Td>{!last && <Checkbox size="xs" checked={r.n === 1} aria-label={`jump after node ${i + 1}`}
                    onChange={(e) => edit(i, { n: e.currentTarget.checked ? 1 : 20 })} />}</Table.Td>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap">
                      <ActionIcon size="sm" variant="subtle" disabled={i === 0} aria-label={`move node ${i + 1} up`} onClick={() => move(i, -1)}>↑</ActionIcon>
                      <ActionIcon size="sm" variant="subtle" disabled={last} aria-label={`move node ${i + 1} down`} onClick={() => move(i, 1)}>↓</ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" disabled={rows.length <= 2} aria-label={`remove node ${i + 1}`}
                        onClick={() => set(rows.filter((_, j) => j !== i))}>×</ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      <Group gap="xs" align="flex-end">
        <Button size="xs" variant="default" onClick={() => set([...rows.slice(0, -1), ...(rows.length ? [{ ...rows[rows.length - 1], n: 20 }] : []),
          { label: "G", k: [0, 0, 0], n: 1 }])}>Add a node</Button>
        <Button size="xs" variant="default" disabled={!k.data} onClick={() => k.data && onChange({ ...recipe,
          bands: { ...(recipe.bands ?? {}), path: { units: "crystal_b", points: k.data.standard } } })}>Standard path</Button>
        <NumberInput size="xs" w={110} label="Points in all" value={total} min={10} max={2000} step={10} allowDecimal={false}
          onChange={(v) => typeof v === "number" && setTotal(v)} />
        <Button size="xs" variant="default" disabled={!k.data || rows.length < 2}
          onClick={() => k.data && set(spreadPoints(rows, k.data.reciprocal, total))}>Share them by length</Button>
        <Text size="xs" c="dimmed">{points} k-points on the path</Text>
      </Group>
    </Stack>
  );
}

function SpinNote({ recipe }: { recipe: Recipe }) {
  const m = siteMoments(recipe).filter((x) => x.m);
  if (!m.length || recipe.spin !== "none") return null;
  return <Text size="xs" c="orange">The moments are ignored without spin: choose Collinear in Settings.</Text>;
}
